import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * 🔐 CENTRALIZED SITE ACCESS HOOK
 * 
 * Single source of truth for determining which sites a user can access.
 * Uses site_assignments table for non-director roles.
 * 
 * STRATEGY BY ROLE:
 * | Role               | Sites visible                                    |
 * |--------------------|--------------------------------------------------|
 * | company_director   | All company sites (via company_id)               |
 * | project_supervisor | Only sites assigned via site_assignments         |
 * | site_manager       | Only sites assigned via site_assignments         |
 * 
 * USAGE:
 * const { siteIds, companyId, loading } = useAuthorizedSiteIds();
 * 
 * Then use siteIds in your queries:
 * query.in('site_id', siteIds)
 */

interface AuthorizedSiteIdsResult {
  siteIds: string[];
  companyId: string | null;
}

async function fetchAuthorizedSiteIds(
  userId: string,
  role: string | undefined
): Promise<AuthorizedSiteIdsResult> {
  // Get company_id first
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('user_id', userId)
    .single();

  if (profileError || !profile?.company_id) {
    return { siteIds: [], companyId: null };
  }

  const companyId = profile.company_id;

  // company_director sees all company sites
  if (role === 'company_director') {
    const { data: sites } = await supabase
      .from('sites')
      .select('id')
      .eq('company_id', companyId)
      .is('deleted_at', null);
    
    return {
      siteIds: sites?.map(s => s.id) || [],
      companyId,
    };
  }

  // For project_supervisor and site_manager: use site_assignments only
  const { data: assignments } = await supabase
    .from('site_assignments')
    .select('site_id')
    .eq('user_id', userId);

  return {
    siteIds: assignments?.map(a => a.site_id) || [],
    companyId,
  };
}

export const useAuthorizedSiteIds = () => {
  const { user } = useAuth();

  const { data, isLoading, isFetched, error, refetch } = useQuery({
    queryKey: ['authorized-site-ids', user?.id, user?.role],
    queryFn: () => fetchAuthorizedSiteIds(user!.id, user?.role),
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes - assignments change very rarely
    gcTime: 60 * 60 * 1000,   // Keep in cache 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });

  return {
    siteIds: data?.siteIds || [],
    companyId: data?.companyId || null,
    loading: isLoading,
    isFetched,
    error: error?.message || null,
    refetch,
    // Helper to check if a site is accessible
    canAccessSite: (siteId: string) => data?.siteIds.includes(siteId) ?? false,
  };
};

// Export the fetch function for use in other hooks that need to fetch sites
export { fetchAuthorizedSiteIds };
